import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import type { MonthlyRevenueRecord } from '../../../hooks/useMonthlyRevenue';
import { fmtMonth, momBarColor } from '../../../utils/monthlyRevenueUtils';

interface MomChangeChartProps {
  records: MonthlyRevenueRecord[];
}

interface ChartEntry {
  label: string;
  pct: number;
  cappedPct: number;
  isExtreme: boolean;
  invoice_month: string;
}

const MomTooltip: React.FC<{ active?: boolean; payload?: any[]; label?: string }> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d: ChartEntry = payload[0].payload;
  if (!d || d.invoice_month == null) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[160px]">
      <p className="text-[11px] font-bold text-slate-700 mb-1.5">{fmtMonth(d.invoice_month)}</p>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] text-slate-500">MoM Change</span>
        <span className={`text-[12px] font-bold tabular-nums ${(d.pct ?? 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
          {(d.pct ?? 0) >= 0 ? '+' : ''}{(d.pct ?? 0).toFixed(1)}%
        </span>
      </div>
      {d.isExtreme && (
        <p className="text-[10px] text-amber-600 mt-1">Extreme change — bar capped at ±100%</p>
      )}
    </div>
  );
};

export const MomChangeChart: React.FC<MomChangeChartProps> = ({ records }) => {
  const data: ChartEntry[] = records.map(r => ({
    label: fmtMonth(r.invoice_month),
    pct: r.mom_change_percent,
    cappedPct: Math.max(-100, Math.min(100, r.mom_change_percent)),
    isExtreme: Math.abs(r.mom_change_percent) > 100,
    invoice_month: r.invoice_month,
  }));

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
      <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 border-b border-slate-100">
        <div className="w-0.5 h-4 rounded-full bg-emerald-500 flex-none" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
          Month-over-Month Change
        </span>
        <span className="text-[10px] text-slate-400 ml-auto">Capped at ±100% for scale</span>
      </div>

      <div className="p-4">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#64748B' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tickFormatter={v => `${v}%`}
              tick={{ fontSize: 10, fill: '#94A3B8' }}
              axisLine={false}
              tickLine={false}
              width={40}
              domain={[-100, 100]}
            />
            <Tooltip content={<MomTooltip />} cursor={{ fill: '#F8FAFC' }} />
            <ReferenceLine y={0} stroke="#CBD5E1" strokeWidth={1} />
            <Bar dataKey="cappedPct" radius={[3, 3, 0, 0]}>
              {data.map((entry, i) => (
                <Cell key={`cell-${i}`} fill={entry.isExtreme ? '#F59E0B' : momBarColor(entry.pct)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Color legend */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 justify-center">
          {[
            { color: '#10B981', label: '≥ +20%' },
            { color: '#3B82F6', label: '0 to +20%' },
            { color: '#F97316', label: '-20% to 0%' },
            { color: '#EF4444', label: '< -20%' },
            { color: '#F59E0B', label: 'Extreme (>200%)' },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-1.5 text-[10px] text-slate-600">
              <div className="w-2.5 h-2.5 rounded-sm flex-none" style={{ backgroundColor: color }} />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MomChangeChart;
